import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Incassi() {
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: 0, date: new Date().toISOString().slice(0,10), method: "contanti", status: "Incassato" });

  const load = async () => {
    const [p, b, c] = await Promise.all([api.get("/payments"), api.get("/bookings"), api.get("/customers")]);
    setPayments(p.data); setBookings(b.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  // Prenotazioni già marcate Incassato → contribuiscono automaticamente al totale incassato.
  const paidBookings = useMemo(
    () => bookings.filter(b => b.payment_status === "Incassato" && b.status !== "Cancellata"),
    [bookings]
  );
  const paidBookingIds = useMemo(() => new Set(paidBookings.map(b => b.id)), [paidBookings]);

  const totals = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().slice(0,10);
    const monthPrefix = today.slice(0,7);
    const yearPrefix = today.slice(0,4);
    let incassato = 0, today_amt = 0, month_amt = 0, year_amt = 0;

    // 1) Prenotazioni con stato "Incassato": usa booking.date come data incasso
    paidBookings.forEach(b => {
      const amt = Number(b.price || 0);
      const d = b.date || "";
      incassato += amt;
      if (d === today) today_amt += amt;
      if (d.startsWith(monthPrefix)) month_amt += amt;
      if (d.startsWith(yearPrefix)) year_amt += amt;
    });

    // 2) Payments manuali: conta solo quelli SENZA booking Incassato collegato
    //    (per non duplicare quando un pagamento è stato registrato per una prenotazione già in stato Incassato).
    payments.forEach(p => {
      if (p.booking_id && paidBookingIds.has(p.booking_id)) return;
      const amt = Number(p.amount || 0);
      const d = p.date || "";
      incassato += amt;
      if (d === today) today_amt += amt;
      if (d.startsWith(monthPrefix)) month_amt += amt;
      if (d.startsWith(yearPrefix)) year_amt += amt;
    });

    const daIncassare = bookings.filter(b => (b.payment_status === "Da incassare" || b.payment_status === "Parzialmente incassato") && b.status !== "Cancellata")
      .reduce((s,b)=>s + Number(b.price||0), 0);
    return { incassato, today_amt, month_amt, year_amt, daIncassare };
  }, [payments, bookings, paidBookings, paidBookingIds]);

  const save = async () => {
    try {
      await api.post("/payments", {
        ...form,
        amount: Number(form.amount) || 0,
        booking_id: form.booking_id || undefined,
        customer_id: form.customer_id || undefined,
      });
      toast.success("Incasso registrato"); setOpen(false); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if(!confirm("Eliminare?")) return;
    await api.delete(`/payments/${id}`); load();
  };

  const bookingLabel = (id) => { const b = bookings.find(x=>x.id===id); return b ? `${b.booking_number} · ${b.passenger_name||""}` : "-"; };

  // Righe virtuali dalle prenotazioni Incassate (senza payment collegato)
  const bookingRows = useMemo(() => {
    const paymentBookingIds = new Set(payments.map(p => p.booking_id).filter(Boolean));
    return paidBookings
      .filter(b => !paymentBookingIds.has(b.id))
      .map(b => ({
        id: `booking-${b.id}`,
        date: b.date,
        method: (b.payment_method || "-").toLowerCase(),
        status: "Incassato",
        amount: Number(b.price || 0),
        booking_id: b.id,
        _virtual: true,
      }));
  }, [paidBookings, payments]);

  const allRows = useMemo(() => {
    return [...payments, ...bookingRows].sort((a,b) => (b.date||"").localeCompare(a.date||""));
  }, [payments, bookingRows]);

  return (
    <div className="space-y-5" data-testid="incassi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Incassi & Pagamenti</h1>
        <Button data-testid="new-payment-btn" onClick={()=>{setForm({amount:0, date:new Date().toISOString().slice(0,10), method:"contanti", status:"Incassato"}); setOpen(true);}} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Plus size={16} strokeWidth={3}/> Nuovo Incasso</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Da incassare" value={fmtEUR(totals.daIncassare)} tone="rose"/>
        <Card label="Totale incassato" value={fmtEUR(totals.incassato)} tone="emerald"/>
        <Card label="Oggi" value={fmtEUR(totals.today_amt)} tone="amber"/>
        <Card label="Mese" value={fmtEUR(totals.month_amt)} tone="sky"/>
        <Card label="Anno" value={fmtEUR(totals.year_amt)} tone="purple"/>
      </div>

      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="text-left p-3">Data</th><th>Prenotazione</th><th>Metodo</th><th>Stato</th>
                <th className="text-right">Importo</th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {allRows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">Nessun incasso</td></tr>}
              {allRows.map(p => (
                <tr key={p.id} className="hover:bg-slate-900/40" data-testid={p._virtual ? "incasso-row-booking" : "incasso-row-payment"}>
                  <td className="p-3 text-slate-300">{p.date}</td>
                  <td className="text-slate-300">
                    {bookingLabel(p.booking_id)}
                    {p._virtual && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-mono-num">(da prenotazione)</span>}
                  </td>
                  <td className="text-slate-400">{p.method}</td>
                  <td><StatusBadge status={p.status}/></td>
                  <td className="text-right text-emerald-400 font-mono-num">{fmtEUR(p.amount)}</td>
                  <td className="pr-3 text-right">
                    {p._virtual ? (
                      <span className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-num pr-1">auto</span>
                    ) : (
                      <button onClick={()=>remove(p.id)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400"><Trash2 size={14}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-white">Nuovo Incasso</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Prenotazione</Label>
              <Select value={form.booking_id || "none"} onValueChange={v=>setForm({...form, booking_id: v==="none"?"":v})}>
                <SelectTrigger className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  <SelectItem value="none">-- Nessuna --</SelectItem>
                  {bookings.map(b => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.passenger_name} · {fmtEUR(b.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Data</Label><Input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className="bg-slate-900 border-slate-800"/></div>
            <div><Label className="text-xs text-slate-500">Importo</Label><Input type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} className="bg-slate-900 border-slate-800"/></div>
            <div>
              <Label className="text-xs text-slate-500">Metodo</Label>
              <Select value={form.method} onValueChange={v=>setForm({...form,method:v})}>
                <SelectTrigger className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {["contanti","pos","bonifico","carta","altro"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Stato</Label>
              <Select value={form.status} onValueChange={v=>setForm({...form,status:v})}>
                <SelectTrigger className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {["Da incassare","Parzialmente incassato","Incassato"].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="save-payment-btn">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, tone="amber" }) {
  const t = { amber:"text-amber-400", rose:"text-rose-400", emerald:"text-emerald-400", sky:"text-sky-400", purple:"text-purple-400" };
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/60 p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <div className={`mt-2 font-mono-num text-xl font-bold ${t[tone]}`}>{value}</div>
    </div>
  );
}
